import { createContext, useContext, type FC, type ReactNode } from 'react';

/**
 * Low-frequency UI state context.
 * Contains state that changes infrequently (only at start/end of streaming).
 *
 * This context is designed for performance optimization:
 * - Contains only boolean/simple state that changes at streaming boundaries
 * - Does NOT update on every token (eliminates cascade re-renders)
 * - Consumed by: ChatInput (for disable/enable), Loading indicators, Error displays
 * - Now includes streaming message data for StreamingAgentMessage component
 */
interface ChatUIStateContextValue {
  /**
   * Boolean indicating whether ANY message is currently being streamed.
   * Changes only at start/end of streaming (NOT on every token).
   * Used by ChatInput to disable submit button during streaming.
   */
  isStreaming: boolean;

  /**
   * Current streaming message ID if any message is being streamed.
   * Used to identify which message is actively receiving tokens.
   * null when no streaming is active.
   */
  streamingMessageId: string | null;

  /**
   * Global error state for chat functionality.
   * Contains user-facing error messages when operations fail.
   */
  error: string | null;

  /**
   * Loading state for operations that don't involve streaming.
   * Used for initial conversation loading, citations enhancement, etc.
   */
  isLoading: boolean;

  /**
   * Warning shown when a loaded conversation has no valid previous response ID resulting
   * in that the context will be resetted.
   */
  noPreviousContextWarning: boolean;
}

const ChatUIStateContext = createContext<ChatUIStateContextValue | undefined>(
  undefined,
);

interface ChatUIStateProviderProps {
  children: ReactNode;
  isStreaming: boolean;
  streamingMessageId: string | null;
  error: string | null;
  isLoading: boolean;
  noPreviousContextWarning: boolean;
}

/**
 * Provider for low-frequency UI state.
 * This context only updates when streaming starts/stops or errors occur.
 * It never updates during token-by-token streaming to prevent unnecessary re-renders.
 */
export const ChatUIStateProvider: FC<ChatUIStateProviderProps> = ({
  children,
  isStreaming,
  streamingMessageId,
  error,
  isLoading,
  noPreviousContextWarning,
}) => {
  const contextValue: ChatUIStateContextValue = {
    isStreaming,
    streamingMessageId,
    error,
    isLoading,
    noPreviousContextWarning,
  };

  return (
    <ChatUIStateContext.Provider value={contextValue}>
      {children}
    </ChatUIStateContext.Provider>
  );
};

/**
 * Hook to access low-frequency UI state.
 * Components using this hook will only re-render when streaming starts/stops
 * or error states change, NOT during token-by-token streaming updates.
 */
export const useChatUIState = (): ChatUIStateContextValue => {
  const context = useContext(ChatUIStateContext);
  if (context === undefined) {
    throw new Error('useChatUIState must be used within a ChatUIStateProvider');
  }
  return context;
};
